import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, CheckCircle, X, Crosshair, SpinnerGap, ArrowClockwise } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import useGeolocation from '../hooks/useGeolocation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal blocking the seller right after login until they pick which location
 * they'll be working at today. Also reusable from the Sidebar "Ubicación" item
 * to switch mid-shift.
 *
 * NEW (iter49): Attempts native GPS auto-detection first. If the seller is
 * within an active selling point's `radius_meters`, that location is
 * auto-selected and the modal closes without any manual input. Fallback to
 * the manual list on GPS failure/denial/no-match.
 */
const LocationSelectorModal = ({ open, onClose, isInitialPrompt = false }) => {
    const { user, fetchUser } = useAuth();
    const [locations, setLocations] = useState([]);
    const [selectedId, setSelectedId] = useState(user?.current_location_id || '');
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [gpsState, setGpsState] = useState({ phase: 'idle', message: null, distance: null });
    // phase: idle | requesting | matched | no_match | denied | error
    const geo = useGeolocation();

    // Only sellers auto-detect. Admins/managers use manual selection intentionally.
    const shouldAutoDetect = user?.role === 'seller' && isInitialPrompt;

    const commitLocation = useCallback(async (locationId, { silent = false } = {}) => {
        setSaving(true);
        try {
            await axios.patch(`${API}/users/me/current-location`, { location_id: locationId });
            await fetchUser();
            sessionStorage.setItem(`location_selected_${user?.id}`, locationId);
            if (!silent) {
                const picked = locations.find(l => l.id === locationId);
                toast.success(`Trabajando desde ${picked?.name || 'ubicación seleccionada'}`);
            }
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudo guardar la ubicación');
        } finally {
            setSaving(false);
        }
    }, [fetchUser, locations, onClose, user?.id]);

    const runGpsDetection = useCallback(async () => {
        setGpsState({ phase: 'requesting', message: 'Detectando tu ubicación…', distance: null });
        try {
            const coords = await geo.request({ highAccuracy: true, timeout: 8000 });
            const { data } = await axios.post(`${API}/locations/resolve-gps`, {
                latitude: coords.latitude,
                longitude: coords.longitude,
            });
            if (data.matched && data.location) {
                setGpsState({
                    phase: 'matched',
                    message: `📍 ${data.location.name}`,
                    distance: data.distance_m,
                });
                // Auto-commit with a short toast so it feels native
                toast.success(`📍 Ubicación detectada: ${data.location.name}`);
                await commitLocation(data.location.id, { silent: true });
            } else {
                setGpsState({
                    phase: 'no_match',
                    message: 'No estás dentro del radio de ningún punto de venta configurado.',
                    distance: data.candidates?.[0]?.distance_m ?? null,
                });
            }
        } catch (err) {
            const code = err?.code || 'error';
            const msg = code === 'permission_denied'
                ? 'Permiso de ubicación denegado. Selecciona manualmente.'
                : code === 'unsupported'
                    ? 'Tu navegador no soporta GPS. Selecciona manualmente.'
                    : code === 'timeout'
                        ? 'GPS tardó demasiado. Selecciona manualmente.'
                        : 'No se pudo obtener tu ubicación. Selecciona manualmente.';
            setGpsState({
                phase: code === 'permission_denied' ? 'denied' : 'error',
                message: msg,
                distance: null,
            });
        }
    }, [geo, commitLocation]);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        axios.get(`${API}/locations`)
            .then(({ data }) => {
                const actives = (data || []).filter(l => l.is_active !== false);
                setLocations(actives);
                if (!selectedId && actives.length === 1) {
                    setSelectedId(actives[0].id);
                }
                // After locations are loaded, kick off auto-detection for sellers
                if (shouldAutoDetect && actives.some(l => l.latitude != null && l.longitude != null)) {
                    // Fire-and-forget: state transitions drive UI
                    runGpsDetection();
                }
            })
            .catch(() => toast.error('No se pudieron cargar las ubicaciones'))
            .finally(() => setLoading(false));
    }, [open]);

    const handleConfirm = async () => {
        if (!selectedId) {
            toast.error('Selecciona una ubicación');
            return;
        }
        await commitLocation(selectedId);
    };

    if (!open) return null;

    const canDismiss = !isInitialPrompt && !!user?.current_location_id;
    const isDetecting = gpsState.phase === 'requesting';

    return (
        <div
            className="fixed inset-0 bg-[#1F2D4B]/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            data-testid="location-selector-modal"
        >
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl border border-[#ECE3D2] relative">
                {canDismiss && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-3 top-3 text-[#8B8474] hover:text-[#1F2D4B] p-1"
                        data-testid="location-modal-close"
                    >
                        <X size={20} weight="bold" />
                    </button>
                )}
                <div className="p-6 sm:p-7">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-11 h-11 rounded-xl bg-[#FB7185]/15 flex items-center justify-center">
                            <MapPin size={22} weight="duotone" className="text-[#FB7185]" />
                        </div>
                        <div>
                            <h2 className="font-display text-xl font-bold text-[#1F2D4B]">
                                {isInitialPrompt ? '¡Bienvenida!' : 'Cambiar ubicación'}
                            </h2>
                            <p className="text-xs text-[#8B8474]">
                                {isInitialPrompt
                                    ? '¿Desde dónde trabajarás hoy?'
                                    : 'Selecciona tu nueva ubicación de trabajo.'}
                            </p>
                        </div>
                    </div>

                    {/* GPS auto-detection banner */}
                    {shouldAutoDetect && gpsState.phase !== 'idle' && (
                        <div
                            className={`mb-4 p-3 rounded-xl border text-sm flex items-start gap-3 ${
                                gpsState.phase === 'requesting'
                                    ? 'bg-[#EEF6FF] border-[#3B82F6]/30 text-[#1E40AF]'
                                    : gpsState.phase === 'matched'
                                        ? 'bg-[#ECFDF5] border-[#059669]/30 text-[#065F46]'
                                        : 'bg-[#FFFBEB] border-[#F59E0B]/30 text-[#92400E]'
                            }`}
                            data-testid={`gps-banner-${gpsState.phase}`}
                        >
                            {isDetecting
                                ? <SpinnerGap size={18} weight="bold" className="animate-spin flex-shrink-0 mt-0.5" />
                                : gpsState.phase === 'matched'
                                    ? <CheckCircle size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
                                    : <Crosshair size={18} weight="bold" className="flex-shrink-0 mt-0.5" />}
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold" data-testid="gps-status-message">{gpsState.message}</p>
                                {gpsState.distance != null && gpsState.phase !== 'matched' && (
                                    <p className="text-[11px] mt-0.5">
                                        Punto más cercano a {Math.round(gpsState.distance)} m
                                    </p>
                                )}
                                {gpsState.phase === 'matched' && gpsState.distance != null && (
                                    <p className="text-[11px] mt-0.5">A {Math.round(gpsState.distance)} m del punto.</p>
                                )}
                            </div>
                            {(gpsState.phase === 'no_match' || gpsState.phase === 'error' || gpsState.phase === 'denied') && (
                                <button
                                    type="button"
                                    onClick={runGpsDetection}
                                    disabled={isDetecting}
                                    data-testid="gps-retry-btn"
                                    className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/60 border border-current/20 text-xs font-semibold hover:bg-white transition"
                                    title="Reintentar detección GPS"
                                >
                                    <ArrowClockwise size={12} weight="bold" /> Reintentar
                                </button>
                            )}
                        </div>
                    )}

                    <p className="text-sm text-[#4B5878] mb-4">
                        Cada venta que registres quedará asociada a esta ubicación.
                    </p>

                    {loading ? (
                        <div className="py-8 text-center text-sm text-[#8B8474]">Cargando ubicaciones…</div>
                    ) : locations.length === 0 ? (
                        <div className="py-8 text-center text-sm text-[#8B8474]">
                            No hay ubicaciones activas. Avisa al administrador.
                        </div>
                    ) : (
                        <div className="space-y-2 max-h-[50vh] overflow-y-auto -mx-1 px-1" data-testid="location-list">
                            {locations.map(l => (
                                <button
                                    key={l.id}
                                    type="button"
                                    onClick={() => setSelectedId(l.id)}
                                    data-testid={`location-option-${l.id}`}
                                    className={`w-full text-left px-4 py-3 rounded-xl border transition-all flex items-center justify-between ${
                                        selectedId === l.id
                                            ? 'border-[#1F2D4B] bg-[#1F2D4B]/5 ring-2 ring-[#1F2D4B]/15'
                                            : 'border-[#ECE3D2] hover:border-[#1F2D4B]/40 hover:bg-[#F8F4EC]'
                                    }`}
                                >
                                    <div className="min-w-0">
                                        <p className="font-semibold text-[#1F2D4B] truncate">{l.name}</p>
                                        {l.address && (
                                            <p className="text-[11px] text-[#8B8474] truncate">{l.address}</p>
                                        )}
                                    </div>
                                    {selectedId === l.id && (
                                        <CheckCircle size={22} weight="fill" className="text-[#65C76A] flex-shrink-0 ml-3" />
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Manual GPS trigger — helpful for non-sellers and iOS retry scenarios */}
                    {!shouldAutoDetect && locations.some(l => l.latitude != null && l.longitude != null) && (
                        <button
                            type="button"
                            onClick={runGpsDetection}
                            disabled={isDetecting || saving}
                            data-testid="gps-detect-btn"
                            className="mt-3 w-full h-11 rounded-xl border border-[#3B82F6]/30 bg-[#EEF6FF] text-[#1E40AF] font-semibold hover:bg-[#DBEAFE] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {isDetecting
                                ? <><SpinnerGap size={16} className="animate-spin" /> Detectando…</>
                                : <><Crosshair size={16} weight="bold" /> Usar mi ubicación GPS</>}
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!selectedId || saving || locations.length === 0}
                        data-testid="location-confirm-btn"
                        className="mt-3 w-full h-12 rounded-xl bg-[#1F2D4B] text-white font-semibold hover:bg-[#0F1B3D] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {saving ? 'Guardando…' : 'Trabajaré desde aquí'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LocationSelectorModal;
